import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useCubeRestructureApi } from 'services/v1/SystemTenant/AROCube/CubeRestructureService';
import { AxiosDefaultErrorEntity } from 'types/api/Common/ErrorTypes';
import {
  RequestCubeRestructureCheck,
  RequestRestructureCube,
  ResponseCubeRestructureCheck,
  ResponseRestructureCube,
} from 'types/api/SystemTenant/AROCube/CubeRestructureTypes';

export const restructureStorageKey = (tenantId: string) => `cube-restructure:${tenantId}`;

function readPending(key: string): RequestRestructureCube | null {
  try {
    const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
    const targetFields: Record<string, string> = {
      CHANGE_MEMBER: 'memberId',
      CHANGE_TIME_PERIOD_SET: 'timePeriodSetId',
      CHANGE_LAYER_SET: 'cubeLayerSetId',
    };
    const target = targetFields[saved?.restructureType];
    if (
      target &&
      ['cubeId', 'operationId', 'checkToken', target].every((field) => typeof saved[field] === 'string' && saved[field])
    ) {
      return saved;
    }
  } catch {
    // A missing or invalid saved operation must not prevent opening the form.
  }
  return null;
}

function savePending(key: string, request: RequestRestructureCube | null) {
  try {
    if (request) sessionStorage.setItem(key, JSON.stringify(request));
    else sessionStorage.removeItem(key);
  } catch {
    // The current dialog still retains the request if browser storage is unavailable.
  }
}

const message = (error: unknown) =>
  (error as AxiosDefaultErrorEntity)?.response?.data?.message || 'Unable to complete the request. Please try again.';
const statusCode = (error: unknown) => (error as AxiosDefaultErrorEntity)?.response?.status;
const isFinished = (result: ResponseRestructureCube) => result.status === 'COMPLETED' || result.status === 'CANCELLED';

export function useCubeRestructureFlow(systemTenantId: string, payload: RequestCubeRestructureCheck | null) {
  const api = useCubeRestructureApi();
  const { mutateAsync: check } = api.check;
  const { mutateAsync: execute } = api.execute;
  const { mutateAsync: getStatus } = api.status;
  const { mutateAsync: retry } = api.retry;
  const { mutateAsync: cancel } = api.cancel;
  const storageKey = restructureStorageKey(systemTenantId);
  const [request, setRequest] = useState(() => readPending(storageKey));
  const [result, setResult] = useState<ResponseRestructureCube | null>(null);
  const [preview, setPreview] = useState<{
    key: string;
    payload: RequestCubeRestructureCheck;
    data: ResponseCubeRestructureCheck;
  } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [checkingKey, setCheckingKey] = useState<string | null>(null);
  const inFlight = useRef(false);
  const pendingCheck = useRef<{ key: string; sequence: number } | null>(null);
  const automaticCheckKey = useRef<string | null>(null);
  const previewSequence = useRef(0);
  const restored = useRef(false);
  const mounted = useRef(true);
  const payloadKey = JSON.stringify(payload);
  const previousPayloadKey = useRef(payloadKey);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (previousPayloadKey.current === payloadKey) return;
    previousPayloadKey.current = payloadKey;
    if (request) return;
    previewSequence.current++;
    setPreview(null);
    setError('');
    setCheckingKey(null);
  }, [payloadKey, request]);

  const checked = preview?.key === payloadKey ? preview : null;
  const checking = checkingKey === payloadKey;
  const canExecute = Boolean(
    payload &&
      checked?.data.canRestructure &&
      checked.data.checkToken &&
      !checked.data.blockers.length &&
      !request &&
      !busy &&
      !checking
  );

  const checkData = useCallback(async () => {
    const selection = JSON.parse(payloadKey) as RequestCubeRestructureCheck | null;
    if (
      !selection ||
      request ||
      (pendingCheck.current?.key === payloadKey && pendingCheck.current.sequence === previewSequence.current)
    )
      return;
    const sequence = ++previewSequence.current;
    pendingCheck.current = { key: payloadKey, sequence };
    setCheckingKey(payloadKey);
    setPreview(null);
    setError('');
    try {
      const data = await check(selection);
      if (mounted.current && sequence === previewSequence.current) {
        if (data.cubeId !== selection.cubeId || data.restructureType !== selection.restructureType) {
          setError('The data check does not match the selected cube. Please check again.');
          return;
        }
        setPreview({ key: payloadKey, payload: selection, data });
      }
    } catch (failure) {
      if (mounted.current && sequence === previewSequence.current) setError(message(failure));
    } finally {
      if (mounted.current && sequence === previewSequence.current) {
        pendingCheck.current = null;
        setCheckingKey(null);
      }
    }
  }, [payloadKey, request, check]);

  useEffect(() => {
    if (request) return;
    if (payloadKey === 'null') {
      automaticCheckKey.current = null;
      return;
    }
    if (automaticCheckKey.current === payloadKey) return;
    automaticCheckKey.current = payloadKey;
    void checkData();
  }, [payloadKey, request, checkData]);

  const perform = useCallback(
    async (action: 'execute' | 'status' | 'retry' | 'cancel', saved = request) => {
      if (!saved || inFlight.current) return;
      inFlight.current = true;
      setBusy(true);
      setError('');
      let executing = action === 'execute';
      const accept = (response: ResponseRestructureCube) => {
        savePending(storageKey, isFinished(response) ? null : saved);
        if (mounted.current) setResult(response);
      };
      try {
        if (action === 'execute') accept(await execute(saved));
        else if (action === 'status') accept(await getStatus(saved.operationId));
        else if (action === 'cancel') accept(await cancel(saved.operationId));
        else if (result) accept(await retry(saved.operationId));
        else {
          // A lost execute response may hide a committed operation. Look it up first.
          let recorded: ResponseRestructureCube | null = null;
          try {
            recorded = await getStatus(saved.operationId);
          } catch (failure) {
            if (statusCode(failure) !== 404) throw failure;
          }
          if (!mounted.current) return;
          if (recorded) accept(isFinished(recorded) ? recorded : await retry(saved.operationId));
          else {
            executing = true;
            // Use the exact persisted request and idempotency key, never a new operation.
            accept(await execute(saved));
          }
        }
      } catch (failure) {
        if (!mounted.current) return;
        if (executing && [400, 409, 422].includes(statusCode(failure) || 0)) {
          try {
            accept(await getStatus(saved.operationId));
          } catch (lookupFailure) {
            if (statusCode(lookupFailure) === 404) {
              savePending(storageKey, null);
              setRequest(null);
              setPreview(null);
              previewSequence.current++;
            }
          }
        }
        setError(message(failure));
      } finally {
        inFlight.current = false;
        if (mounted.current) setBusy(false);
      }
    },
    [request, result, storageKey, execute, getStatus, retry, cancel]
  );

  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    if (request) void perform('status');
  }, [request, perform]);

  useEffect(() => {
    if (result?.status !== 'IN_PROGRESS' || busy || error) return;
    const timeout = setTimeout(() => void perform('retry'), 3000);
    return () => clearTimeout(timeout);
  }, [result, busy, error, perform]);

  const start = () => {
    if (!canExecute || !checked || inFlight.current) return;
    const saved: RequestRestructureCube = {
      ...checked.payload,
      checkToken: checked.data.checkToken,
      operationId: uuidv4(),
    };
    savePending(storageKey, saved);
    setRequest(saved);
    void perform('execute', saved);
  };

  return {
    request,
    result,
    error,
    busy,
    canExecute,
    checkData,
    start,
    checking,
    preview: checked?.data,
    retry: () => perform('retry'),
    refreshStatus: () => perform('status'),
    cancel: () => perform('cancel'),
  };
}
